import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaArrowRight,
  FaGoogle,
  FaFacebook,
} from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { authUserAPI } from "../../../../services/adminApi";
import "./Auth.css";

const SignUp = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phoneCountryCode: "+234", // Default to Nigeria
    password: "",
    confirmPassword: "",
    referralSource: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.firstName || !formData.lastName) {
      toast.error("Please enter your full name");
      return false;
    }
    if (!formData.email) {
      toast.error("Please enter your email address");
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!formData.phone) {
      toast.error("Please enter your phone number");
      return false;
    }
    // Remove country code from phone for validation (if user includes it)
    const phoneWithoutCode = formData.phone.replace(/^\+?\d{1,3}/, "");
    if (phoneWithoutCode.length < 7) {
      toast.error("Please enter a valid phone number (at least 7 digits)");
      return false;
    }
    if (!formData.password) {
      toast.error("Please create a password");
      return false;
    }
    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      // Combine country code with phone number if not already included
      let fullPhone = formData.phone;
      // If phone doesn't start with +, add the country code
      if (!fullPhone.startsWith("+")) {
        // Remove any leading zeros before adding country code
        const cleanPhone = fullPhone.replace(/^0+/, "");
        fullPhone = formData.phoneCountryCode + cleanPhone;
      }

      const response = await authUserAPI.register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: fullPhone,
        password: formData.password,
        referralSource: formData.referralSource,
      });

      console.log("📦 Raw response:", response);

      const isSuccess = response && (response.success === true || response._id);

      if (isSuccess) {
        toast.success(
          response.message ||
            "Account created successfully! Please check your email to verify your account."
        );

        localStorage.setItem("pendingVerificationEmail", formData.email);
        
        setTimeout(() => {
          navigate("/verify-email");
        }, 1500);
      } else {
        console.log("Response success is not true:", response);
        toast.error(response?.message || "Registration failed");
      }
    } catch (err) {
      console.error("❌ Signup error:", err);

      const statusCode = err.response?.status;
      const errorData = err.response?.data;

      console.log("Status Code:", statusCode);
      console.log("Error Data:", errorData);

      let errorMessage = "Registration failed. Please try again.";

      if (errorData) {
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }

      if (statusCode === 400) {
        if (errorData?.errorType === "DuplicateError") {
          errorMessage =
            "User with this email already exists. Please use a different email or login.";
        } else if (errorData?.errorType === "ValidationError") {
          errorMessage = errorData.error || errorData.message || "Please check your input and try again.";
        }
      } else if (statusCode === 401) {
        errorMessage = "Unauthorized. Please check your credentials.";
      } else if (statusCode === 403) {
        errorMessage = "Access forbidden. Please contact support.";
      } else if (statusCode === 404) {
        errorMessage = "Service not found. Please try again later.";
      } else if (statusCode === 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (statusCode === 408 || err.code === "ECONNABORTED") {
        errorMessage = "Request timed out. Please check your connection.";
      }

      console.log("Displaying error message:", errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-bg-decoration">
        <div className="auth-bg-circle auth-bg-circle-1"></div>
        <div className="auth-bg-circle auth-bg-circle-2"></div>
        <div className="auth-bg-circle auth-bg-circle-3"></div>
      </div>

      <div className="auth-card" data-aos="fade-up">
        <div className="auth-card-header">
          <div className="auth-logo">
            <span className="auth-logo-icon">🏠</span>
            <span className="auth-logo-text">
              TAYE'S <span className="auth-logo-gold">PROPERTY</span>
            </span>
          </div>
          <h1 className="auth-title">Create Account</h1>
          <p className="auth-subtitle">
            Join us to start your property journey
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-form-row">
            <div className="auth-input-group">
              <FaUser className="auth-input-icon" />
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                className="auth-input"
                disabled={loading}
              />
            </div>
            <div className="auth-input-group">
              <FaUser className="auth-input-icon" />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                className="auth-input"
                disabled={loading}
              />
            </div>
          </div>

          <div className="auth-input-group">
            <FaEnvelope className="auth-input-icon" />
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              className="auth-input"
              disabled={loading}
            />
          </div>

          {/* Phone Input with Country Code Selector */}
<div className="phone-input-wrapper">
  <select
    name="phoneCountryCode"
    value={formData.phoneCountryCode}
    onChange={handleChange}
    className="country-code-select"
    disabled={loading}
  >
    <option value="+234">🇳🇬 +234</option>
    <option value="+1">🇺🇸 +1</option>
    <option value="+44">🇬🇧 +44</option>
    <option value="+91">🇮🇳 +91</option>
    <option value="+86">🇨🇳 +86</option>
    <option value="+81">🇯🇵 +81</option>
    <option value="+49">🇩🇪 +49</option>
    <option value="+33">🇫🇷 +33</option>
    <option value="+61">🇦🇺 +61</option>
    <option value="+55">🇧🇷 +55</option>
    <option value="+27">🇿🇦 +27</option>
    <option value="+254">🇰🇪 +254</option>
    <option value="+233">🇬🇭 +233</option>
    <option value="+20">🇪🇬 +20</option>
    <option value="+971">🇦🇪 +971</option>
    <option value="+966">🇸🇦 +966</option>
    <option value="+65">🇸🇬 +65</option>
    <option value="+60">🇲🇾 +60</option>
    <option value="+63">🇵🇭 +63</option>
    <option value="+62">🇮🇩 +62</option>
    <option value="+34">🇪🇸 +34</option>
    <option value="+39">🇮🇹 +39</option>
    <option value="+31">🇳🇱 +31</option>
    <option value="+46">🇸🇪 +46</option>
    <option value="+47">🇳🇴 +47</option>
    <option value="+45">🇩🇰 +45</option>
    <option value="+358">🇫🇮 +358</option>
    <option value="+41">🇨🇭 +41</option>
    <option value="+43">🇦🇹 +43</option>
    <option value="+32">🇧🇪 +32</option>
    <option value="+351">🇵🇹 +351</option>
    <option value="+30">🇬🇷 +30</option>
    <option value="+90">🇹🇷 +90</option>
    <option value="+52">🇲🇽 +52</option>
    <option value="+54">🇦🇷 +54</option>
    <option value="+56">🇨🇱 +56</option>
    <option value="+57">🇨🇴 +57</option>
    <option value="+58">🇻🇪 +58</option>
    <option value="+64">🇳🇿 +64</option>
    <option value="+82">🇰🇷 +82</option>
    <option value="+66">🇹🇭 +66</option>
    <option value="+84">🇻🇳 +84</option>
  </select>
  
  <div className="auth-input-group" style={{ flex: 1, margin: 0 }}>
    <FaPhone className="auth-input-icon" style={{ left: '12px' }} />
    <input
      type="tel"
      name="phone"
      placeholder="Phone Number"
      value={formData.phone}
      onChange={handleChange}
      className="auth-input phone-input"
      style={{ paddingLeft: '45px' }}
      disabled={loading}
    />
  </div>
</div>

          <div className="auth-input-group">
            <FaLock className="auth-input-icon" />
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              className="auth-input"
              disabled={loading}
            />
            <button
              type="button"
              className="auth-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <div className="auth-input-group">
            <FaLock className="auth-input-icon" />
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleChange}
              className="auth-input"
              disabled={loading}
            />
            <button
              type="button"
              className="auth-password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <div className="auth-input-group">
            <select
              name="referralSource"
              value={formData.referralSource}
              onChange={handleChange}
              className="auth-select"
              disabled={loading}
            >
              <option value="">How did you hear about us? (Optional)</option>
              <option value="google">Google Search</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="friend">Friend/Family</option>
              <option value="direct">Direct Visit</option>
              <option value="other">Other</option>
            </select>
          </div>

          <button type="submit" className="auth-btn" disabled={loading}>
            {loading ? "Creating Account..." : "Sign Up"}
            <FaArrowRight className="auth-btn-icon" />
          </button>
        </form>

        <div className="auth-divider">
          <span>Or sign up with</span>
        </div>

        <div className="auth-social">
          <button className="auth-social-btn google">
            <FaGoogle /> Google
          </button>
          <button className="auth-social-btn facebook">
            <FaFacebook /> Facebook
          </button>
        </div>

        <p className="auth-footer">
          Already have an account?{" "}
          <Link to="/login" className="auth-link">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignUp;